import { Box, Button, Group, Stack, Text } from "@mantine/core"
import { ArrowLeftIcon, CubeIcon, XIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { DisabledReason, HelpButtonGroup } from "@dehub/theme"
import { renderBusinessName } from "../../utils/render-business-name"
import { DnStatusBadge } from "../active-deliveries/atoms"
import { DeliveryActionButton } from "../delivery/action-controls"
import { DotPill, Tone } from "../delivery/atoms"
import type { DeliveryActionState } from "../delivery/delivery-actions"
import { DnDetail, DnPriority, formatDateTime } from "./dn-detail-utils"
import classes from "./dn-detail.module.css"

const PRIORITY_TONE: Record<DnPriority, Tone> = { VIP: "red", URGENT: "amber", NORMAL: "grey" }
const CHANNEL_TONE: Record<string, Tone> = {
  LOCAL_DELIVERY: "green",
  SUPPLY_FREIGHT: "amber",
  CUSTOMER_PICKUP: "blue",
  CROSS_BORDER: "blue",
}

export function PriorityPill({ priority }: Readonly<{ priority: DnPriority }>) {
  const { t } = useTranslation("delivery")
  return <DotPill tone={PRIORITY_TONE[priority]}>{t(`manage.priority.${priority}`)}</DotPill>
}

export function ChannelPill({ channel }: Readonly<{ channel: string | null | undefined }>) {
  const { t } = useTranslation("delivery")
  if (!channel) return <>-</>
  return <DotPill tone={CHANNEL_TONE[channel] ?? "grey"}>{t(`badges.channel.${channel}`, { defaultValue: "-" })}</DotPill>
}

type Props = Readonly<{
  dn: DnDetail
  priority: DnPriority
  channel: string | null
  cancelState: DeliveryActionState
  onBack: () => void
  onCancel: () => void
}>

/** Breadcrumb + header card: DN code, status / priority / channel pills, buyer · order · created, «Цуцлах». */
export function DnDetailHeader({ dn, priority, channel, cancelState, onBack, onCancel }: Props) {
  const { t } = useTranslation("delivery")
  return (
    <>
      <Group gap="sm">
        <Button variant="subtle" color="gray" leftSection={<ArrowLeftIcon size={16} />} onClick={onBack}>
          {t("dnDetail.back")}
        </Button>
        <Text className={classes.crumb}>
          {t("dnDetail.breadcrumb")} /{" "}
          <Text span className={classes.mono} c="var(--nw-text)" translate="no">
            {dn.refCode ?? "-"}
          </Text>
        </Text>
      </Group>

      <Box className={classes.headerCard}>
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <Group gap="md" align="center" wrap="nowrap" flex={1} miw={0}>
            <Box className={classes.iconBox}>
              <CubeIcon size={26} />
            </Box>
            <Stack gap={6} miw={0}>
              <Group gap="sm" wrap="wrap" align="center">
                <Text className={classes.dnTitle} translate="no">
                  {dn.refCode ?? "-"}
                </Text>
                <DnStatusBadge status={dn.deliveryNoteStatus} />
                <PriorityPill priority={priority} />
                {channel && <ChannelPill channel={channel} />}
              </Group>
              <Text className={classes.subtitle}>
                {renderBusinessName(dn.receiverBusiness, "-")}
                {dn.salesCode && (
                  <>
                    {` · ${t("dnDetail.orderPrefix")} `}
                    <Text span className={classes.mono} c="var(--nw-text)" translate="no">
                      {dn.salesCode}
                    </Text>
                  </>
                )}
                {dn.createdAt && (
                  <>
                    {` · ${t("dnDetail.createdPrefix")}: `}
                    <Text span ff="monospace" fz="var(--nw-fz-mono)">
                      {formatDateTime(dn.createdAt)}
                    </Text>
                  </>
                )}
              </Text>
            </Stack>
          </Group>
          {/* @dehub/help: attached «?» (R2) + reason popover while unavailable (R4). */}
          <DisabledReason
            helpKey="order.supplier-delivery-detail.reason.cancel"
            reason={cancelState.allowed ? null : /эрх|permission/i.test(cancelState.reason ?? "") ? "PERM" : "NOT_IN_STATUS"}
            when={!cancelState.allowed}
            position="bottom-end"
          >
            <HelpButtonGroup helpKey="order.supplier-delivery-detail.action.cancel">
              <DeliveryActionButton data-help-id="action-cancel" state={cancelState} variant="default" leftSection={<XIcon size={16} />} onClick={onCancel}>
                {t("dnDetail.cancel")}
              </DeliveryActionButton>
            </HelpButtonGroup>
          </DisabledReason>
        </Group>
      </Box>
    </>
  )
}
